import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { GerenciarErrosService, ErroNbs, ClasseErrosIgnorados } from './gerenciar-erros.service';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'app-gerenciar-erros',
  templateUrl: './gerenciar-erros.component.html',
  styleUrls: ['./gerenciar-erros.component.scss']
})
export class GerenciarErrosComponent implements OnInit, OnDestroy {

  erros: ErroNbs[] = [];
  errosFiltrados: ErroNbs[] = [];
  loading: boolean = false;
  cols: any[];
  erroSelecionado: ErroNbs | null = null;
  displayDialog: boolean = false;
  globalFilter: string = '';

  // Estatísticas
  totalErros: number = 0;
  errosRecentes24h: number = 0;
  errosRecentes7d: number = 0;

  // Modal de ignorar erros
  displayDialogIgnorar: boolean = false;
  errosIgnorados: ClasseErrosIgnorados[] = [];
  classesDisponiveis: { classe: string; metodo: string; mensagens: string[]; selecionado: boolean; expandido: boolean; idErroRepresentativo: number }[] = [];
  classesSelecionadas: { classe: string; metodo: string; idErro: number }[] = [];
  loadingIgnorar: boolean = false;
  selecionarTodos: boolean = false;
  classesIgnoradasExpandidas: Record<string, boolean> = {};
  
  private routerSubscription: Subscription;

  constructor(
    private service: GerenciarErrosService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private router: Router
  ) { }

  ngOnInit() {
    this.cols = [
      { field: 'id', header: 'ID', width: '80px' },
      { field: 'dataOcorrencia', header: 'Data/Hora', width: '180px' },
      { field: 'mensagem', header: 'Mensagem', width: '400px' },
      { field: 'classe', header: 'Classe', width: '300px' },
      { field: 'metodo', header: 'Método', width: '150px' },
      { field: 'path', header: 'Endpoint', width: '250px' },
      { field: 'payload', header: 'Payload', width: '250px' },
      { field: 'params', header: 'Parâmetros', width: '250px' },
      { field: 'httpMethod', header: 'HTTP', width: '100px' }
    ];

    this.carregarErros();
    this.routerSubscription = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      if (event.url.includes('gerenciar-erros') || event.urlAfterRedirects.includes('gerenciar-erros')) {
        this.carregarErros();
      }
    });
  }

  ngOnDestroy() {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  carregarErros() {
    this.loading = true;
    this.service.listarErros().subscribe({
      next: (res) => {
        this.erros = res;
        this.errosFiltrados = res;
        this.calcularEstatisticas();
        this.loading = false;
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Erro ao carregar lista de erros'
        });
        this.loading = false;
      }
    });
  }

  calcularEstatisticas() {
    this.totalErros = this.erros.length;
    
    const agora = new Date();
    const umDiaAtras = new Date(agora.getTime() - 24 * 60 * 60 * 1000);
    const seteDiasAtras = new Date(agora.getTime() - 7 * 24 * 60 * 60 * 1000);

    this.errosRecentes24h = this.erros.filter(erro => 
      new Date(erro.dataOcorrencia) > umDiaAtras
    ).length;

    this.errosRecentes7d = this.erros.filter(erro => 
      new Date(erro.dataOcorrencia) > seteDiasAtras
    ).length;
  }

  verDetalhes(erro: ErroNbs) {
    this.erroSelecionado = erro;
    this.displayDialog = true;
  }

  confirmarDelecao(erro: ErroNbs) {
    this.confirmationService.confirm({
      message: `Tem certeza que deseja deletar o erro #${erro.id}?`,
      header: 'Confirmar Deleção',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sim, deletar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.deletarErro(erro.id);
      }
    });
  }

  deletarErro(id: number) {
    this.service.deletarErro(id).subscribe({
      next: (res) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Erro deletado com sucesso'
        });
        this.carregarErros();
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: err.error?.message || 'Erro ao deletar registro'
        });
      }
    });
  }

  confirmarDeletarTodos() {
    if (this.erros.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'Não há erros para deletar'
      });
      return;
    }

    this.confirmationService.confirm({
      message: `Tem certeza que deseja deletar TODOS os ${this.erros.length} erros? Esta ação não pode ser desfeita!`,
      header: 'Confirmar Deleção em Massa',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sim, deletar todos',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.deletarTodos();
      }
    });
  }

  deletarTodos() {
    this.loading = true;
    let deletados = 0;
    let errosDeletados = 0;

    this.erros.forEach((erro, index) => {
      this.service.deletarErro(erro.id).subscribe({
        next: () => {
          deletados++;
          if (deletados + errosDeletados === this.erros.length) {
            this.finalizarDelecaoMassa(deletados, errosDeletados);
          }
        },
        error: () => {
          errosDeletados++;
          if (deletados + errosDeletados === this.erros.length) {
            this.finalizarDelecaoMassa(deletados, errosDeletados);
          }
        }
      });
    });
  }

  finalizarDelecaoMassa(deletados: number, erros: number) {
    this.loading = false;
    if (erros === 0) {
      this.messageService.add({
        severity: 'success',
        summary: 'Sucesso',
        detail: `${deletados} erro(s) deletado(s) com sucesso`
      });
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: 'Concluído com avisos',
        detail: `${deletados} deletados, ${erros} falharam`
      });
    }
    this.carregarErros();
  }

  filtrarGlobal(event: any) {
    const valor = event.target.value.toLowerCase();
    if (!valor) {
      this.errosFiltrados = this.erros;
      return;
    }

    this.errosFiltrados = this.erros.filter(erro => 
      erro.mensagem?.toLowerCase().includes(valor) ||
      erro.classe?.toLowerCase().includes(valor) ||
      erro.metodo?.toLowerCase().includes(valor) ||
      erro.path?.toLowerCase().includes(valor) ||
      erro.arquivo?.toLowerCase().includes(valor)
    );
  }

  private formatarValorJson(valor: any): string {
    if (valor === null || valor === undefined) return '-';

    if (typeof valor === 'string') {
      const texto = valor.trim();
      if (!texto) return '-';
      try {
        const parsed = JSON.parse(texto);
        return JSON.stringify(parsed, null, 2);
      } catch {
        return valor;
      }
    }

    try {
      return JSON.stringify(valor, null, 2);
    } catch {
      return String(valor);
    }
  }

  formatarValorDetalhe(valor: any): string {
    return this.formatarValorJson(valor);
  }

  resumirValor(valor: any, limite: number = 80): string {
    const texto = this.formatarValorJson(valor);
    if (texto.length <= limite) return texto;
    return `${texto.slice(0, limite - 3)}...`;
  }

  formatarData(data: string): string {
    if (!data) return '-';
    const dt = new Date(data);
    return dt.toLocaleString('pt-BR');
  }

  copiarStacktrace() {
    if (!this.erroSelecionado?.stacktrace) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'Não há stacktrace para copiar'
      });
      return;
    }

    const texto = this.erroSelecionado.stacktrace;

    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(texto).then(() => {
        this.messageService.add({
          severity: 'success',
          summary: 'Copiado',
          detail: 'Stacktrace copiado para a área de transferência'
        });
      }).catch(() => {
        this.copiarStacktraceFallback(texto);
      });
    } else {
      this.copiarStacktraceFallback(texto);
    }
  }

  private copiarStacktraceFallback(texto: string) {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = texto;
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      const sucesso = document.execCommand('copy');
      document.body.removeChild(textarea);

      if (sucesso) {
        this.messageService.add({
          severity: 'success',
          summary: 'Copiado',
          detail: 'Stacktrace copiado para a área de transferência'
        });
      } else {
        throw new Error('execCommand falhou');
      }
    } catch (error) {
      this.messageService.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'Não foi possível copiar o stacktrace'
      });
    }
  }

  exportarParaTxt() {
    if (this.erros.length === 0) return;

    let conteudo = 'RELATÓRIO DE ERROS - PORTAL NBS\n';
    conteudo += '='.repeat(80) + '\n';
    conteudo += `Data de Exportação: ${new Date().toLocaleString('pt-BR')}\n`;
    conteudo += `Total de Erros: ${this.totalErros}\n`;
    conteudo += `Erros nas últimas 24h: ${this.errosRecentes24h}\n`;
    conteudo += `Erros nos últimos 7 dias: ${this.errosRecentes7d}\n`;
    conteudo += '='.repeat(80) + '\n\n';

    this.erros.forEach((erro, index) => {
      conteudo += `ERRO #${erro.id}\n`;
      conteudo += `-`.repeat(80) + '\n';
      conteudo += `Data/Hora: ${this.formatarData(erro.dataOcorrencia)}\n`;
      conteudo += `Mensagem: ${erro.mensagem}\n`;
      conteudo += `Classe: ${erro.classe}\n`;
      conteudo += `Método: ${erro.metodo}\n`;
      conteudo += `Linha: ${erro.linha}\n`;
      conteudo += `Arquivo: ${erro.arquivo}\n`;
      conteudo += `Endpoint: ${erro.path}\n`;
      conteudo += `HTTP Method: ${erro.httpMethod}\n`;
      conteudo += `Payload: ${erro.payload}\n`;
      conteudo += `Parâmetros: ${erro.params}\n`;
      conteudo += `\nStacktrace:\n${erro.stacktrace}\n`;
      conteudo += '\n' + '='.repeat(80) + '\n\n';
    });

    const blob = new Blob([conteudo], { type: 'text/plain;charset=utf-8' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = `erros_portal_${new Date().toISOString().split('T')[0]}.txt`;
    link.click();
  }

  abrirModalIgnorarErros() {
    this.displayDialogIgnorar = true;
    this.carregarErrosIgnorados();
    this.prepararListaErrosParaIgnorar();
  }

  carregarErrosIgnorados() {
    this.loadingIgnorar = true;
    this.service.listarErrosIgnorados().subscribe({
      next: (res) => {
        this.errosIgnorados = res;
        this.classesIgnoradasExpandidas = {};
        this.loadingIgnorar = false;
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Erro ao carregar lista de erros ignorados'
        });
        this.loadingIgnorar = false;
      }
    });
  }

  prepararListaErrosParaIgnorar() {
    // Agrupa mensagens por classe+método e armazena ID representativo
    const classesMap = new Map<string, { classe: string; metodo: string; mensagens: Set<string>; idErro: number }>();

    this.erros.forEach(erro => {
      const chave = this.getChaveClasseMetodo(erro.classe, erro.metodo);
      if (!classesMap.has(chave)) {
        classesMap.set(chave, {
          classe: erro.classe,
          metodo: erro.metodo,
          mensagens: new Set<string>(),
          idErro: erro.id // Armazena o ID do primeiro erro encontrado dessa classe
        });
      }
      classesMap.get(chave).mensagens.add(erro.mensagem);
    });

    const classesIgnoradas = new Set(
      this.errosIgnorados.map(e => this.getChaveClasseMetodo(e.classe, e.metodo))
    );

    this.classesDisponiveis = Array.from(classesMap.values())
      .filter(item => !classesIgnoradas.has(this.getChaveClasseMetodo(item.classe, item.metodo)))
      .map(item => ({
        classe: item.classe,
        metodo: item.metodo,
        mensagens: Array.from(item.mensagens),
        selecionado: false,
        expandido: false,
        idErroRepresentativo: item.idErro
      }));

    this.classesSelecionadas = [];
    this.selecionarTodos = false;
  }

  toggleClasseSelecionada(classeWrapper: { classe: string; metodo: string; mensagens: string[]; selecionado: boolean; expandido: boolean; idErroRepresentativo: number }, event?: Event) {
    event?.stopPropagation();

    classeWrapper.selecionado = !classeWrapper.selecionado;

    if (classeWrapper.selecionado) {
      const chave = this.getChaveClasseMetodo(classeWrapper.classe, classeWrapper.metodo);
      if (!this.classesSelecionadas.some(c => this.getChaveClasseMetodo(c.classe, c.metodo) === chave)) {
        this.classesSelecionadas.push({
          classe: classeWrapper.classe,
          metodo: classeWrapper.metodo,
          idErro: classeWrapper.idErroRepresentativo
        });
      }
    } else {
      const chave = this.getChaveClasseMetodo(classeWrapper.classe, classeWrapper.metodo);
      this.classesSelecionadas = this.classesSelecionadas.filter(
        c => this.getChaveClasseMetodo(c.classe, c.metodo) !== chave
      );
    }

    this.selecionarTodos = this.classesDisponiveis.length > 0 &&
      this.classesSelecionadas.length === this.classesDisponiveis.length;
  }

  toggleExpandClasseDisponivel(classeWrapper: { classe: string; metodo: string; mensagens: string[]; selecionado: boolean; expandido: boolean; idErroRepresentativo: number }, event?: Event) {
    event?.stopPropagation();
    classeWrapper.expandido = !classeWrapper.expandido;
  }

  toggleExpandClasseIgnorada(classe: string, event?: Event) {
    event?.stopPropagation();
    this.classesIgnoradasExpandidas[classe] = !this.classesIgnoradasExpandidas[classe];
  }

  isClasseIgnoradaExpandida(classe: string): boolean {
    return !!this.classesIgnoradasExpandidas[classe];
  }

  toggleSelecionarTodos() {
    const valorAtual = this.selecionarTodos;

    this.classesDisponiveis.forEach(classe => {
      classe.selecionado = valorAtual;
    });

    if (valorAtual) {
      this.classesSelecionadas = this.classesDisponiveis.map(c => ({
        classe: c.classe,
        metodo: c.metodo,
        idErro: c.idErroRepresentativo
      }));
    } else {
      this.classesSelecionadas = [];
    }
  }

  ignorarErrosSelecionados() {
    if (this.classesSelecionadas.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'Selecione pelo menos uma classe para ignorar'
      });
      return;
    }

    this.confirmationService.confirm({
      message: `Tem certeza que deseja ignorar ${this.classesSelecionadas.length} classe(s)/método(s)?`,
      header: 'Confirmar Ignorar Classe(s)/Método(s)',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sim, ignorar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-warning',
      accept: () => {
        this.executarIgnorarClasses();
      }
    });
  }

  private executarIgnorarClasses() {
    this.loadingIgnorar = true;
    let processados = 0;
    let errosCount = 0;

    this.classesSelecionadas.forEach(item => {
      this.service.ignorarClassePorErroId(item.idErro).subscribe({
        next: (response) => {
          processados++;
          if (processados + errosCount === this.classesSelecionadas.length) {
            this.finalizarIgnorarErros(processados, errosCount);
          }
        },
        error: (err) => {
          errosCount++;
          if (processados + errosCount === this.classesSelecionadas.length) {
            this.finalizarIgnorarErros(processados, errosCount);
          }
        }
      });
    });
  }

  finalizarIgnorarErros(processados: number, errosCount: number) {
    this.loadingIgnorar = false;
    if (errosCount === 0) {
      this.messageService.add({
        severity: 'success',
        summary: 'Sucesso',
        detail: `${processados} classe(s) ignorada(s) com sucesso. Todos os erros dessas classes foram removidos.`
      });
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: 'Concluído com avisos',
        detail: `${processados} classe(s) ignorada(s), ${errosCount} falharam`
      });
    }
    // Recarrega os dados e atualiza as classes disponíveis
    this.recarregarDadosIgnorados();
    this.classesSelecionadas = [];
  }

  removerClasseIgnorada(erroIgnorado: ClasseErrosIgnorados) {
    this.confirmationService.confirm({
      message: `Tem certeza que deseja remover a classe <strong>${erroIgnorado.classe}</strong> da lista de ignorados?<br><br>
                Após a remoção, novos erros dessa classe voltarão a ser registrados.`,
      header: 'Confirmar Remoção',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sim, remover',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-warning',
      accept: () => {
        this.loadingIgnorar = true;
        this.service.removerClasseIgnorada(erroIgnorado.idIgnorado).subscribe({
          next: (response) => {
            this.messageService.add({
              severity: 'success',
              summary: 'Sucesso',
              detail: response.message || 'Classe removida da lista de ignorados com sucesso'
            });
            this.recarregarDadosIgnorados();
          },
          error: (err) => {
            this.messageService.add({
              severity: 'error',
              summary: 'Erro',
              detail: err.error?.message || 'Erro ao remover da lista de ignorados'
            });
            this.loadingIgnorar = false;
          }
        });
      }
    });
  }

  /**
   * Recarrega os dados de erros e erros ignorados, depois atualiza a lista de classes disponíveis
   */
  private recarregarDadosIgnorados() {
    this.loadingIgnorar = true;
    let errosCarregados = false;
    let ignoradosCarregados = false;

    // Carrega a lista de erros
    this.service.listarErros().subscribe({
      next: (res) => {
        this.erros = res;
        this.errosFiltrados = res;
        this.calcularEstatisticas();
        errosCarregados = true;
        
        // Se ambos foram carregados, atualiza a lista de classes disponíveis
        if (errosCarregados && ignoradosCarregados) {
          this.prepararListaErrosParaIgnorar();
          this.loadingIgnorar = false;
        }
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Erro ao carregar lista de erros'
        });
        this.loadingIgnorar = false;
      }
    });

    // Carrega a lista de erros ignorados
    this.service.listarErrosIgnorados().subscribe({
      next: (res) => {
        this.errosIgnorados = res;
        this.classesIgnoradasExpandidas = {};
        ignoradosCarregados = true;
        
        // Se ambos foram carregados, atualiza a lista de classes disponíveis
        if (errosCarregados && ignoradosCarregados) {
          this.prepararListaErrosParaIgnorar();
          this.loadingIgnorar = false;
        }
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Erro ao carregar lista de erros ignorados'
        });
        this.loadingIgnorar = false;
      }
    });
  }

  formatarDataIgnorado(data: string): string {
    if (!data) return '-';
    const dt = new Date(data);
    return dt.toLocaleString('pt-BR');
  }

  private getChaveClasseMetodo(classe: string, metodo: string): string {
    return `${classe}::${metodo}`;
  }
}

